import { useCallback, useEffect, useId, useState } from "react";

let activeOverflowId: string | null = null;
const overflowListeners = new Set<(activeId: string | null) => void>();

function broadcastOverflowActive(activeId: string | null) {
  activeOverflowId = activeId;
  overflowListeners.forEach((fn) => fn(activeId));
}

function subscribeOverflow(
  listener: (activeId: string | null) => void
): () => void {
  overflowListeners.add(listener);
  return () => overflowListeners.delete(listener);
}

/** Active id while an Editor add-element / add-template react-overlays Dropdown is open. */
export const EDITOR_FLOATING_DROPDOWN_OVERFLOW_ID = "__editorFloatingDropdown";

/**
 * "More element types" (⋯) inside {@link SelectElementType} while the add-element
 * dropdown is open. Must be whitelisted in Editor so opening it does not close the
 * parent react-overlays Dropdown (which would drop the portal and break outside-click).
 */
export const EDITOR_ADD_ELEMENT_MORE_OVERFLOW_ID = "__editorAddElementMore";

/** Active id while a react-select menu is open in a builder property panel. */
export const BUILDER_REACT_SELECT_MENU_OVERFLOW_ID = "__builderReactSelectMenu";

/** Color pickers, custom list dropdowns, etc. inside a panel (closes ⋯ and react-selects). */
export const BUILDER_INLINE_SUBMENU_OVERFLOW_ID = "__builderInlineSubmenu";

export function broadcastExclusiveOverflowMenuActive(
  activeId: string | null
): void {
  broadcastOverflowActive(activeId);
}

export function subscribeExclusiveOverflowMenuActive(
  listener: (activeId: string | null) => void
): () => void {
  return subscribeOverflow(listener);
}

export function getExclusiveOverflowMenuActiveId(): string | null {
  return activeOverflowId;
}

/**
 * Ensures only one builder “⋯” overflow / extra-row section is open at a time
 * across ColMenu, spacing, border, cart font, background, editor kebab, etc.
 */
export function useBuilderExclusiveOverflowMenu(): {
  isOpen: boolean;
  setIsOpen: (next: boolean) => void;
  toggle: () => void;
  /** Use to avoid treating this instance’s ⋯ as “foreign” when coordinating submenus. */
  overflowInstanceId: string;
} {
  const instanceId = useId().replace(/:/g, "");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const unsub = subscribeOverflow((activeId) => {
      if (activeId !== instanceId) {
        setIsOpen(false);
      }
    });
    return () => {
      unsub();
      if (activeOverflowId === instanceId) {
        broadcastOverflowActive(null);
      }
    };
  }, [instanceId]);

  const setIsOpenExclusive = useCallback(
    (next: boolean) => {
      if (next) {
        broadcastOverflowActive(instanceId);
        setIsOpen(true);
      } else {
        if (activeOverflowId === instanceId) {
          broadcastOverflowActive(null);
        }
        setIsOpen(false);
      }
    },
    [instanceId]
  );

  const toggle = useCallback(() => {
    setIsOpen((wasOpen) => {
      if (wasOpen) {
        if (activeOverflowId === instanceId) {
          broadcastOverflowActive(null);
        }
        return false;
      }
      broadcastOverflowActive(instanceId);
      return true;
    });
  }, [instanceId]);

  return {
    isOpen,
    setIsOpen: setIsOpenExclusive,
    toggle,
    overflowInstanceId: instanceId,
  };
}
